import { Active, DragOverlay, Modifier, Modifiers, useDndContext } from "@dnd-kit/core"
import { snapCenterToCursor } from "@dnd-kit/modifiers"
import { CSSProperties, useMemo } from "react"
import { getDragAttributeInfo } from "../../hooks/use-drag-drop"
import { kContainerClass } from "../container/container-constants"
import DragIndicator from "../../assets/icons/drag-indicator.svg"

import "./attribute-drag-overlay.scss"

function createRestrictAboveContainerTop(): Modifier {
  let containerTop: number | undefined
  return ({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect) return transform
    if (containerTop === undefined) {
      const container = document.querySelector(`.${kContainerClass}`)
      containerTop = container?.getBoundingClientRect().top ?? undefined
    }
    if (containerTop !== undefined && draggingNodeRect.top + transform.y < containerTop) {
      return { ...transform, y: containerTop - draggingNodeRect.top }
    }
    return transform
  }
}

function getOverlayDragId(active: Active | null, instanceId: string, excludeRegEx?: RegExp) {
  const activeId = `${active?.id}`
  return active && activeId.startsWith(instanceId) && !excludeRegEx?.test(activeId)
    ? activeId : undefined
}

interface IProps {
  dragIdPrefix: string
  dragIdExcludeRegEx?: RegExp
  overlayHeight?: number
  overlayWidth?: number
  xOffset?: number
  yOffset?: number
}

export function AttributeDragOverlay ({
  dragIdPrefix, dragIdExcludeRegEx, overlayHeight, overlayWidth, xOffset, yOffset
}: IProps) {
  const { active } = useDndContext()
  const { dataSet, attributeId: dragAttrId, snapToCursor } = getDragAttributeInfo(active) || {}

  const activeDragId = getOverlayDragId(active, dragIdPrefix, dragIdExcludeRegEx)
  const attr = activeDragId && dragAttrId ? dataSet?.attrFromID(dragAttrId) : undefined
  // const handleDropAnimation = (/*params: any*/) => {
  //   /**
  //    * If there has been no drop we would like to animate the overlay back to its original position.
  //    * Otherwise, we don't want to animate it at all.
  //    */
  // }

  // Drags initiated by plugins can specify the size of the overlay
  const style: CSSProperties | undefined = useMemo(() => activeDragId && overlayHeight && overlayWidth
    ? { height: `${overlayHeight}px`, width: `${overlayWidth}px` }
    : undefined, [activeDragId, overlayHeight, overlayWidth])

  // Build modifiers array: optionally snap to cursor, optionally add plugin offset
  const modifiers: Modifiers | undefined = useMemo(() => {
    const mods: Modifiers = []
    if (snapToCursor) mods.push(snapCenterToCursor)
    // Drags initiated by plugins have to be offset based on the location of the plugin
    if (xOffset || yOffset) {
      mods.push(args => {
        const { x, y, scaleX, scaleY } = args.transform
        return {
          x: x + (xOffset ?? 0),
          y: y + (yOffset ?? 0),
          scaleX, scaleY
        }
      })
    }
    mods.push(createRestrictAboveContainerTop())
    return mods
  }, [snapToCursor, xOffset, yOffset])

  // Trigger a re-render at the end of the drag.
  // Otherwise, dnd-kit doesn't always clean up the overlay properly.
  const key = active?.id || "none"

  return (
    <DragOverlay
      className="dnd-kit-drag-overlay"
      // dropAnimation={handleDropAnimation}
      modifiers={modifiers}
      style={style}
      key={key}
    >
      {attr
        ? <div className="attribute-drag-overlay">
            <DragIndicator className="drag-indicator-icon" aria-hidden="true" />
            {attr.name}
          </div>
        : null}
    </DragOverlay>
  )
}

// This DnDKit component shows up as `Unknown` in tools like WhyDidYouRender
DragOverlay.displayName = "DragOverlay"
